import { type LoggerPublisherService } from "@ledgerhq/device-management-kit";

import { configTypes } from "./config/di/configTypes";
import { type ContextModuleConstructorArgs } from "./config/model/ContextModuleBuildArgs";
import {
  type ContextModuleCalConfig,
  type ContextModuleDatasourceConfig,
  type ContextModuleMetadataServiceConfig,
  type ContextModuleServiceConfig,
} from "./config/model/ContextModuleConfig";
import { HttpProxyDataSource } from "./modules/ethereum/proxy/data/HttpProxyDataSource";
import { HttpSafeProxyDataSource } from "./modules/ethereum/proxy/data/HttpSafeProxyDataSource";
import { proxyTypes } from "./modules/ethereum/proxy/di/proxyTypes";
import { HttpTrustedNameDataSource } from "./modules/ethereum/trusted-name/data/HttpTrustedNameDataSource";
import { type TrustedNameDataSource } from "./modules/ethereum/trusted-name/data/TrustedNameDataSource";
import { trustedNameTypes } from "./modules/ethereum/trusted-name/di/trustedNameTypes";
import { type ContextLoader } from "./shared/domain/ContextLoader";
import { ContextModuleChainID } from "./shared/domain/ContextModuleChainID";
import { ContextModuleBuilder } from "./ContextModuleBuilder";
import { DefaultContextModule } from "./DefaultContextModule";

describe("ContextModuleBuilder", () => {
  const defaultCalConfig: ContextModuleCalConfig = {
    url: "https://cal/v1",
    mode: "prod",
    branch: "main",
  };
  const defaultWeb3ChecksConfig = {
    url: "https://web3checks/v1",
  };
  const mockLoggerFactory = () => ({
    debug: vi.fn(),
    info: vi.fn(),
    warn: vi.fn(),
    error: vi.fn(),
    subscribers: [],
  });

  const defaultBuilderArgs: ContextModuleConstructorArgs = {
    originToken: "test",
    loggerFactory: mockLoggerFactory,
  };

  beforeEach(() => {
    vi.resetAllMocks();
  });

  it("should return a default context module", () => {
    const contextModuleBuilder = new ContextModuleBuilder(defaultBuilderArgs);

    const res = contextModuleBuilder
      .setChain(ContextModuleChainID.Ethereum)
      .build();

    expect(res).toBeInstanceOf(DefaultContextModule);
  });

  it("should return a custom context module", () => {
    const contextModuleBuilder = new ContextModuleBuilder(defaultBuilderArgs);
    const customLoader: ContextLoader = {
      load: vi.fn(),
      canHandle: vi.fn() as unknown as ContextLoader["canHandle"],
    };

    const res = contextModuleBuilder
      .removeDefaultLoaders()
      .addLoader(customLoader)
      .setChain(ContextModuleChainID.Ethereum)
      .build();

    expect(res).toBeInstanceOf(DefaultContextModule);
  });

  it("should return a custom context module with a custom typed data loader", () => {
    const contextModuleBuilder = new ContextModuleBuilder(defaultBuilderArgs);
    const customLoader = { load: vi.fn() };

    const res = contextModuleBuilder
      .removeDefaultLoaders()
      .addTypedDataLoader(customLoader)
      .setChain(ContextModuleChainID.Ethereum)
      .build();

    expect(res).toBeInstanceOf(DefaultContextModule);
    // @ts-expect-error _typedDataLoader is private
    expect(res["_typedDataLoader"]).toBe(customLoader);
  });

  it("should return a custom context module with a custom config", () => {
    const contextModuleBuilder = new ContextModuleBuilder(defaultBuilderArgs);

    const res = contextModuleBuilder
      .setCalConfig(defaultCalConfig)
      .setWeb3ChecksConfig(defaultWeb3ChecksConfig)
      .setChain(ContextModuleChainID.Ethereum)
      .build();
    const config = (res as DefaultContextModule)[
      "_container"
    ].get<ContextModuleServiceConfig>(configTypes.Config);

    expect(res).toBeInstanceOf(DefaultContextModule);
    expect(config.cal).toEqual(defaultCalConfig);
    expect(config.web3checks).toEqual(defaultWeb3ChecksConfig);
  });

  it("should not throw when setChain is called before build", () => {
    const contextModuleBuilder = new ContextModuleBuilder(defaultBuilderArgs);

    expect(() =>
      contextModuleBuilder.setChain(ContextModuleChainID.Ethereum).build(),
    ).not.toThrow();
  });

  it("should throw when setChain is not called before build", () => {
    const contextModuleBuilder = new ContextModuleBuilder(defaultBuilderArgs);

    expect(() => contextModuleBuilder.build()).toThrow(
      "[ContextModuleBuilder] setChain() must be called before build()",
    );
  });

  describe("setMetadataServiceConfig", () => {
    it("should set the metadata service configuration", () => {
      const contextModuleBuilder = new ContextModuleBuilder(defaultBuilderArgs);
      const customMetadataConfig: ContextModuleMetadataServiceConfig = {
        url: "https://custom-metadata-service.com/v3",
      };

      const res = contextModuleBuilder
        .setMetadataServiceConfig(customMetadataConfig)
        .setChain(ContextModuleChainID.Ethereum)
        .build();
      const config = (res as DefaultContextModule)[
        "_container"
      ].get<ContextModuleServiceConfig>(configTypes.Config);

      expect(res).toBeInstanceOf(DefaultContextModule);
      expect(config.metadataServiceDomain).toEqual(customMetadataConfig);
    });

    it("should override the default metadata service configuration", () => {
      const contextModuleBuilder = new ContextModuleBuilder(defaultBuilderArgs);
      const customMetadataConfig: ContextModuleMetadataServiceConfig = {
        url: "https://override-metadata-service.com/v1",
      };

      const res = contextModuleBuilder
        .setMetadataServiceConfig(customMetadataConfig)
        .setChain(ContextModuleChainID.Ethereum)
        .build();
      const config = (res as DefaultContextModule)[
        "_container"
      ].get<ContextModuleServiceConfig>(configTypes.Config);

      expect(config.metadataServiceDomain.url).toBe(customMetadataConfig.url);
      expect(config.metadataServiceDomain.url).not.toBe(
        "https://nft.api.live.ledger.com/v2",
      );
    });
  });

  describe("setCalConfig", () => {
    it("should set the CAL configuration", () => {
      const contextModuleBuilder = new ContextModuleBuilder(defaultBuilderArgs);
      const customCalConfig: ContextModuleCalConfig = {
        url: "https://custom-cal-service.com/v2",
        mode: "test",
        branch: "next",
      };

      const res = contextModuleBuilder
        .setCalConfig(customCalConfig)
        .setChain(ContextModuleChainID.Ethereum)
        .build();
      const config = (res as DefaultContextModule)[
        "_container"
      ].get<ContextModuleServiceConfig>(configTypes.Config);

      expect(res).toBeInstanceOf(DefaultContextModule);
      expect(config.cal).toEqual(customCalConfig);
    });

    it("should override the default CAL configuration", () => {
      const contextModuleBuilder = new ContextModuleBuilder(defaultBuilderArgs);
      const customCalConfig: ContextModuleCalConfig = {
        url: "https://override-cal-service.com/v1",
        mode: "prod",
        branch: "demo",
      };

      const res = contextModuleBuilder
        .setCalConfig(customCalConfig)
        .setChain(ContextModuleChainID.Ethereum)
        .build();
      const config = (res as DefaultContextModule)[
        "_container"
      ].get<ContextModuleServiceConfig>(configTypes.Config);

      expect(config.cal.url).toBe(customCalConfig.url);
      expect(config.cal.mode).toBe(customCalConfig.mode);
      expect(config.cal.branch).toBe(customCalConfig.branch);
      expect(config.cal.url).not.toBe(
        "https://global.api.prd.ledger.com/cal/v1",
      );
    });
  });

  describe("setWeb3ChecksConfig", () => {
    it("should set the web3 checks configuration", () => {
      const contextModuleBuilder = new ContextModuleBuilder(defaultBuilderArgs);
      const customWeb3ChecksConfig = {
        url: "https://custom-web3checks-service.com/v4",
      };

      const res = contextModuleBuilder
        .setWeb3ChecksConfig(customWeb3ChecksConfig)
        .setChain(ContextModuleChainID.Ethereum)
        .build();
      const config = (res as DefaultContextModule)[
        "_container"
      ].get<ContextModuleServiceConfig>(configTypes.Config);

      expect(res).toBeInstanceOf(DefaultContextModule);
      expect(config.web3checks).toEqual(customWeb3ChecksConfig);
    });

    it("should override the default web3 checks configuration", () => {
      const contextModuleBuilder = new ContextModuleBuilder(defaultBuilderArgs);
      const customWeb3ChecksConfig = {
        url: "https://override-web3checks-service.com/v2",
      };

      const res = contextModuleBuilder
        .setWeb3ChecksConfig(customWeb3ChecksConfig)
        .setChain(ContextModuleChainID.Ethereum)
        .build();
      const config = (res as DefaultContextModule)[
        "_container"
      ].get<ContextModuleServiceConfig>(configTypes.Config);

      expect(config.web3checks.url).toBe(customWeb3ChecksConfig.url);
      expect(config.web3checks.url).not.toBe(
        "https://global.api.prd.ledger.com/transaction-checks/v3",
      );
    });
  });

  describe("setDatasourceConfig", () => {
    it("should bind HttpSafeProxyDataSource when proxy is 'safe'", () => {
      const contextModuleBuilder = new ContextModuleBuilder(defaultBuilderArgs);
      const customDatasourceConfig: ContextModuleDatasourceConfig = {
        proxy: "safe",
      };

      const res = contextModuleBuilder
        .setDatasourceConfig(customDatasourceConfig)
        .setChain(ContextModuleChainID.Ethereum)
        .build();
      const proxyDataSource = (res as DefaultContextModule)[
        "_container"
      ].get<unknown>(proxyTypes.ProxyDataSource);

      expect(res).toBeInstanceOf(DefaultContextModule);
      expect(proxyDataSource).toBeInstanceOf(HttpSafeProxyDataSource);
    });

    it("should bind HttpProxyDataSource when proxy is 'default'", () => {
      const contextModuleBuilder = new ContextModuleBuilder(defaultBuilderArgs);
      const customDatasourceConfig: ContextModuleDatasourceConfig = {
        proxy: "default",
      };

      const res = contextModuleBuilder
        .setDatasourceConfig(customDatasourceConfig)
        .setChain(ContextModuleChainID.Ethereum)
        .build();
      const proxyDataSource = (res as DefaultContextModule)[
        "_container"
      ].get<unknown>(proxyTypes.ProxyDataSource);

      expect(res).toBeInstanceOf(DefaultContextModule);
      expect(proxyDataSource).toBeInstanceOf(HttpProxyDataSource);
    });

    it("should bind HttpProxyDataSource when datasource config is empty", () => {
      const contextModuleBuilder = new ContextModuleBuilder(defaultBuilderArgs);
      const customDatasourceConfig: ContextModuleDatasourceConfig = {};

      const res = contextModuleBuilder
        .setDatasourceConfig(customDatasourceConfig)
        .setChain(ContextModuleChainID.Ethereum)
        .build();
      const proxyDataSource = (res as DefaultContextModule)[
        "_container"
      ].get<unknown>(proxyTypes.ProxyDataSource);

      expect(res).toBeInstanceOf(DefaultContextModule);
      expect(proxyDataSource).toBeInstanceOf(HttpProxyDataSource);
    });
  });

  describe("loggerFactory", () => {
    it("should use the default empty logger when loggerFactory is not provided", () => {
      const contextModuleBuilder = new ContextModuleBuilder({
        originToken: "test",
      });

      const res = contextModuleBuilder
        .setChain(ContextModuleChainID.Ethereum)
        .build();
      const loggerFactory = (res as DefaultContextModule)["_container"].get<
        (tag: string) => LoggerPublisherService
      >(configTypes.ContextModuleLoggerFactory);

      expect(res).toBeInstanceOf(DefaultContextModule);
      expect(loggerFactory).toBeDefined();

      const logger = loggerFactory("test");
      expect(logger.debug).toBeDefined();
      expect(logger.info).toBeDefined();
      expect(logger.warn).toBeDefined();
      expect(logger.error).toBeDefined();
      expect(logger.subscribers).toEqual([]);

      expect(() => logger.debug("test")).not.toThrow();
      expect(() => logger.info("test")).not.toThrow();
      expect(() => logger.warn("test")).not.toThrow();
      expect(() => logger.error("test")).not.toThrow();
    });

    it("should set the loggerFactory when provided in the constructor", () => {
      const loggerFactory: (tag: string) => LoggerPublisherService = vi.fn();

      const contextModuleBuilder = new ContextModuleBuilder({
        ...defaultBuilderArgs,
        loggerFactory,
      });

      const res = contextModuleBuilder
        .setChain(ContextModuleChainID.Ethereum)
        .build();
      const boundLoggerFactory = (res as DefaultContextModule)[
        "_container"
      ].get<(tag: string) => LoggerPublisherService>(
        configTypes.ContextModuleLoggerFactory,
      );

      expect(res).toBeInstanceOf(DefaultContextModule);
      expect(boundLoggerFactory).toBe(loggerFactory);
    });

    it("should set the loggerFactory via setLoggerFactory", () => {
      const loggerFactory: (tag: string) => LoggerPublisherService = vi.fn();

      const contextModuleBuilder = new ContextModuleBuilder(defaultBuilderArgs);

      const res = contextModuleBuilder
        .setLoggerFactory(loggerFactory)
        .setChain(ContextModuleChainID.Ethereum)
        .build();
      const boundLoggerFactory = (res as DefaultContextModule)[
        "_container"
      ].get<(tag: string) => LoggerPublisherService>(
        configTypes.ContextModuleLoggerFactory,
      );

      expect(res).toBeInstanceOf(DefaultContextModule);
      expect(boundLoggerFactory).toBe(loggerFactory);
    });

    it("should override constructor loggerFactory with setLoggerFactory", () => {
      const constructorLoggerFactory: (tag: string) => LoggerPublisherService =
        vi.fn();
      const overrideLoggerFactory: (tag: string) => LoggerPublisherService =
        vi.fn();

      const contextModuleBuilder = new ContextModuleBuilder({
        ...defaultBuilderArgs,
        loggerFactory: constructorLoggerFactory,
      });

      const res = contextModuleBuilder
        .setLoggerFactory(overrideLoggerFactory)
        .setChain(ContextModuleChainID.Ethereum)
        .build();
      const boundLoggerFactory = (res as DefaultContextModule)[
        "_container"
      ].get<(tag: string) => LoggerPublisherService>(
        configTypes.ContextModuleLoggerFactory,
      );

      expect(boundLoggerFactory).toBe(overrideLoggerFactory);
      expect(boundLoggerFactory).not.toBe(constructorLoggerFactory);
    });
  });

  describe("instance isolation", () => {
    it("should not share customLoaders arrays between builder instances", () => {
      const builder1 = new ContextModuleBuilder(defaultBuilderArgs);
      const builder2 = new ContextModuleBuilder(defaultBuilderArgs);
      const customLoader: ContextLoader = {
        load: vi.fn(),
        canHandle: vi.fn() as unknown as ContextLoader["canHandle"],
      };

      builder1.addLoader(customLoader);

      const res1 = builder1
        .removeDefaultLoaders()
        .setChain(ContextModuleChainID.Ethereum)
        .build();
      const res2 = builder2
        .removeDefaultLoaders()
        .setChain(ContextModuleChainID.Ethereum)
        .build();

      // @ts-expect-error _loaders is private
      expect(res1["_loaders"]).toHaveLength(1);
      // @ts-expect-error _loaders is private
      expect(res2["_loaders"]).toHaveLength(0);
    });
  });

  describe("setTrustedNameDataSource", () => {
    it("should inject the custom data source into the container", () => {
      const contextModuleBuilder = new ContextModuleBuilder(defaultBuilderArgs);
      const customDataSource: TrustedNameDataSource = {
        getDomainNamePayload: vi.fn(),
        getTrustedNamePayload: vi.fn(),
      };

      const res = contextModuleBuilder
        .setTrustedNameDataSource(customDataSource)
        .setChain(ContextModuleChainID.Ethereum)
        .build();
      const injectedDataSource = (res as DefaultContextModule)[
        "_container"
      ].get<TrustedNameDataSource>(trustedNameTypes.TrustedNameDataSource);

      expect(injectedDataSource).toBe(customDataSource);
    });

    it("should use HttpTrustedNameDataSource when no custom data source is set", () => {
      const contextModuleBuilder = new ContextModuleBuilder(defaultBuilderArgs);

      const res = contextModuleBuilder
        .setChain(ContextModuleChainID.Ethereum)
        .build();
      const injectedDataSource = (res as DefaultContextModule)[
        "_container"
      ].get<TrustedNameDataSource>(trustedNameTypes.TrustedNameDataSource);

      expect(res).toBeInstanceOf(DefaultContextModule);
      expect(injectedDataSource).toBeInstanceOf(HttpTrustedNameDataSource);
    });
  });
});
